import memoize from 'fast-memoize';

type Locale = string | undefined;

export const getNumberFormatter = memoize(Intl.NumberFormat);

export const Presets = {
  decimal: {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
  currency: {
    style: 'currency',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  },
  // Note: this is the same settings as <FormatMoney />
  money: {
    style: 'currency',
    maximumFractionDigits: 2,
  },
} as const;

export type FormatNumberOptions = {
  locale?: Locale;
  preset?: keyof typeof Presets;
  autoHide?: boolean;
} & Partial<Intl.NumberFormatOptions>;

export type FormatNumberValue = number | bigint | Money;

const isMoney = (value: unknown): value is Money =>
  !!(value && typeof value === 'object' && 'amount' in value);

export const formatNumber = (
  locale: Locale,
  value: FormatNumberValue,
  options?: FormatNumberOptions,
): string => {
  const valueAsNumber = isMoney(value) ? value.amount : value;
  const currency = isMoney(value) ? value.currencyCode : options?.currency;

  // Note: ideally, this is done before calling <FormatNumber /> but I'm carrying this
  // behavior forward from <FormatMoney /> / formatMoney() so that we can deprecate FormatMoney.
  if (options?.autoHide && ((!valueAsNumber && valueAsNumber !== 0) || !currency)) {
    return '--';
  }

  const presets = options?.preset && Presets[options.preset];
  const mergedOptions = { ...presets, ...options, currency };

  return getNumberFormatter(locale, mergedOptions).format(valueAsNumber);
};

export const formatMoney = (
  locale: Locale,
  value: FormatNumberValue,
  options?: Omit<FormatNumberOptions, 'preset'>,
) => formatNumber(locale, value, { autoHide: true, preset: 'money', ...options });
